// src/pages/Results2026.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';

// Sheriff Star for COTA badge
const SheriffStar = ({ size = "w-4 h-4" }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21L12 17.77L5.82 21L7 14.14L2 9L8.91 8.26L12 2Z"
      fill="#FFD700"
    />
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const MARQUEE_RACES = {
  monaco:     { name: 'Monaco GP',     badge: '🎲', color: 'text-blue-600',   bgColor: 'bg-blue-100',   title: 'Monaco Dice',              isCustom: false },
  silverstone:{ name: 'British GP',    badge: '⚔️', color: 'text-red-600',    bgColor: 'bg-red-100',    title: 'Silverstone Sword',         isCustom: false },
  spa:        { name: 'Belgian GP',    badge: '🌲', color: 'text-green-600',  bgColor: 'bg-green-100',  title: 'Spa Forest',                isCustom: false },
  monza:      { name: 'Italian GP',    badge: '🏛️', color: 'text-green-600',  bgColor: 'bg-green-100',  title: 'Monza Temple of Speed',     isCustom: false },
  cota:       { name: 'US GP',         badge: <SheriffStar size="w-4 h-4" />, color: 'text-yellow-600', bgColor: 'bg-yellow-100', title: 'Texas Sheriff', isCustom: true },
  abudhabi:   { name: 'Abu Dhabi GP',  badge: '🐪', color: 'text-orange-600', bgColor: 'bg-orange-100', title: 'Abu Dhabi Camel',           isCustom: false },
};

const FEATURE_COLUMNS = [
  { key: 'polePosition',    label: 'POLE', isDriver: true },
  { key: 'podium.0',        label: 'P1',   isDriver: true },
  { key: 'podium.1',        label: 'P2',   isDriver: true },
  { key: 'podium.2',        label: 'P3',   isDriver: true },
  { key: 'podium.3',        label: 'P4',   isDriver: true },
  { key: 'podium.4',        label: 'P5',   isDriver: true },
  { key: 'fastestLap',      label: 'FAST', isDriver: true },
  { key: 'firstRetirement', label: 'DNF',  isDriver: true },
  { key: 'mostPlacesGained',label: 'MPG',  isDriver: true },
  { key: 'driverH2H',       label: 'DR H2H', isDriver: true },
  { key: 'teamH2H',         label: 'TM H2H', isDriver: false },
];

const SPRINT_COLUMNS = [
  { key: 'polePosition',    label: 'POLE', isDriver: true },
  { key: 'podium.0',        label: 'P1',   isDriver: true },
  { key: 'podium.1',        label: 'P2',   isDriver: true },
  { key: 'podium.2',        label: 'P3',   isDriver: true },
  { key: 'fastestLap',      label: 'FAST', isDriver: true },
  { key: 'firstRetirement', label: 'DNF',  isDriver: true },
  { key: 'mostPlacesGained',label: 'MPG',  isDriver: true },
  { key: 'driverH2H',       label: 'DR H2H', isDriver: true },
  { key: 'teamH2H',         label: 'TM H2H', isDriver: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeDriver = (name) => {
  if (!name) return null;
  return name.replace(/_/g, ' ').split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const normalizeTeam = (name) => {
  if (!name) return null;
  return name.replace(/_/g, ' ').split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const driverCode = (name) => {
  if (!name) return '-';
  const norm = normalizeDriver(name);
  if (norm.length <= 3) return norm.toUpperCase();
  const words = norm.split(' ');
  return words.length >= 2
    ? words[words.length - 1].substring(0, 3).toUpperCase()
    : norm.substring(0, 3).toUpperCase();
};

const teamCode = (name) => {
  if (!name || name === 'N/A') return '-';
  const words = name.split(' ');
  return words.length >= 2
    ? words.slice(0, 3).map(w => w.charAt(0)).join('').toUpperCase()
    : name.substring(0, 4).toUpperCase();
};

const getMarqueeInfo = (raceName) => {
  if (!raceName) return null;
  const n = raceName.toLowerCase();
  if (n.includes('monaco'))                                    return MARQUEE_RACES.monaco;
  if (n.includes('british') || n.includes('silverstone'))     return MARQUEE_RACES.silverstone;
  if (n.includes('belgian') || n.includes('belgium'))         return MARQUEE_RACES.spa;
  if (n.includes('monza') || n.includes('italian'))           return MARQUEE_RACES.monza;
  if (n.includes('austin') || n.includes('cota') || n.includes('united states')) return MARQUEE_RACES.cota;
  if (n.includes('abu dhabi'))                                return MARQUEE_RACES.abudhabi;
  return null;
};

// Pull a nested value from predictions using dot-notation key
const getPredValue = (preds, key) => {
  if (!preds) return null;
  if (key.startsWith('podium.')) {
    const idx = parseInt(key.split('.')[1]);
    return preds.podium?.[idx] ?? null;
  }
  if (key === 'driverH2H') return preds.driverHeadToHead?.winner ?? null;
  if (key === 'teamH2H')   return preds.teamHeadToHead?.winner ?? null;
  return preds[key] ?? null;
};

// Pull actual result value using same key convention
const getResultValue = (result, key) => {
  if (!result) return null;
  if (key.startsWith('podium.')) {
    const idx = parseInt(key.split('.')[1]);
    return result.podium?.[idx] ?? null;
  }
  if (key === 'driverH2H') return result.headToHead?.drivers?.winner ?? null;
  if (key === 'teamH2H')   return result.headToHead?.teams?.winner ?? null;
  return result[key] ?? null;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const AccuracyDot = ({ status }) => {
  if (!status) return null;
  if (status === 'correct')   return <span className="text-green-500 font-bold text-sm">✓</span>;
  if (status === 'partial')   return <span className="text-orange-500 font-bold text-sm">~</span>;
  return <span className="text-red-500 font-bold text-sm">✗</span>;
};

const BadgePill = ({ badge }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
    title={`${badge.title} — ${badge.name} Winner`}
  >
    {badge.isCustom
      ? <span className="flex items-center">{badge.badge}</span>
      : <span className="text-sm">{badge.badge}</span>
    }
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────

function Results2026() {
  const { raceId: paramRaceId } = useParams();

  const [races, setRaces]               = useState([]);
  const [allRaces, setAllRaces]         = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState(paramRaceId || '');
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceResult, setRaceResult]     = useState(null);
  const [raceScores, setRaceScores]     = useState([]);
  const [allRaceScores, setAllRaceScores] = useState({});
  const [activeTab, setActiveTab]       = useState('feature'); // 'feature' | 'sprint'
  const [loading, setLoading]           = useState(true);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [error, setError]               = useState('');

  // Score details modal
  const [detailScore, setDetailScore]   = useState(null);

  // ── Helpers ──

  const isPredictionsOpen = useCallback((race) => {
    if (!race) return false;
    const now = new Date();
    if (new Date(race.date) < now) return false;
    if (race.qualifyingDateTime) {
      const closes = new Date(new Date(race.qualifyingDateTime) - 2 * 60 * 1000);
      if (closes < now) return false;
    }
    return true;
  }, []);

  const tipsClosed = useCallback((race) => !isPredictionsOpen(race), [isPredictionsOpen]);

  const getUserBadges = useCallback((userId) => {
    const badges = [];
    const now = new Date();
    allRaces.forEach(race => {
      const info = getMarqueeInfo(race.raceName);
      if (info && (race.status === 'completed' || new Date(race.date) < now)) {
        const scores = allRaceScores[race._id];
        if (scores) {
          const sorted = Object.entries(scores)
            .map(([uid, score]) => ({ uid, score }))
            .sort((a, b) => b.score - a.score);
          if (sorted.length > 0 && sorted[0].uid === userId && sorted[0].score > 0) {
            badges.push(info);
          }
        }
      }
    });
    return badges;
  }, [allRaces, allRaceScores]);

  // ── Data fetching ──

  // 1. Load race list (2026 only, tips closed)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await apiClient.get('/api/f1data/races/all');
        const all2026 = res.data.filter(r => String(r.season) === '2026');
        setAllRaces(all2026);
        const closed = all2026
          .filter(r => tipsClosed(r))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setRaces(closed);
        if (paramRaceId) {
          setSelectedRaceId(paramRaceId);
        } else if (closed.length > 0) {
          setSelectedRaceId(closed[0]._id);
        }
      } catch {
        setError('Failed to load races');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [paramRaceId, tipsClosed]);

  // 2. Load all race scores for badge calculation
  useEffect(() => {
    if (!allRaces.length) return;
    const load = async () => {
      const now = new Date();
      const completed = allRaces.filter(r =>
        r.status === 'completed' || new Date(r.date) < now
      );
      const map = {};
      for (const race of completed) {
        try {
          const res = await apiClient.get(`/api/scores/race/${race._id}`);
          map[race._id] = {};
          (res.data.scores || []).forEach(s => {
            if (s.user && s.score > 0) map[race._id][s.user] = s.score;
          });
        } catch { /* skip */ }
      }
      setAllRaceScores(map);
    };
    load();
  }, [allRaces]);

  // 3. Load selected race data
  useEffect(() => {
    if (!selectedRaceId) return;
    const load = async () => {
      try {
        setScoresLoading(true);
        setRaceResult(null);
        setRaceScores([]);

        const [raceRes, scoresRes] = await Promise.all([
          apiClient.get(`/api/f1data/races/${selectedRaceId}`),
          apiClient.get(`/api/scores/race/${selectedRaceId}`).catch(() => ({ data: { scores: [] } })),
        ]);

        setSelectedRace(raceRes.data);

        // Try to load results (may not exist yet)
        try {
          const resultsRes = await apiClient.get(`/api/results/race/${selectedRaceId}`);
          setRaceResult(resultsRes.data);
        } catch { /* no results yet */ }

        setRaceScores(scoresRes.data.scores || []);

        // Default to sprint tab if sprint race
        if (raceRes.data.isSprint) setActiveTab('sprint');
        else setActiveTab('feature');

      } catch {
        setError('Failed to load race data');
      } finally {
        setScoresLoading(false);
      }
    };
    load();
  }, [selectedRaceId]);

  // ── Render helpers ──

  const getCellAccuracy = (predVal, resultVal, key, preds, result) => {
    if (!predVal || !result) return null;
    // Podium cells get partial credit
    if (key.startsWith('podium.')) {
      if (predVal === resultVal) return 'correct';
      if (result.podium?.includes(predVal)) return 'partial';
      return 'incorrect';
    }
    return predVal === resultVal ? 'correct' : 'incorrect';
  };

  const renderCell = (predVal, resultVal, isDriver, key, preds, result) => {
    const display = isDriver
      ? driverCode(normalizeDriver(predVal))
      : teamCode(normalizeTeam(predVal));

    const accuracy = getCellAccuracy(predVal, resultVal, key, preds, result);
    const title = isDriver ? normalizeDriver(predVal) : normalizeTeam(predVal);

    return (
      <div className="flex items-center justify-center gap-0.5" title={title || ''}>
        <span className="text-xs text-gray-900">{display || '-'}</span>
        {raceResult && <AccuracyDot status={accuracy} />}
      </div>
    );
  };

  const renderResultCell = (result, key, isDriver) => {
    const val = getResultValue(result, key);
    if (!val) return <span className="text-gray-400 text-xs">-</span>;
    const display = isDriver ? driverCode(normalizeDriver(val)) : teamCode(normalizeTeam(val));
    return <span className="text-xs font-semibold text-blue-900" title={isDriver ? normalizeDriver(val) : normalizeTeam(val)}>{display}</span>;
  };

  // Which prediction object to use based on active tab
  const getPreds = (score) => {
    if (activeTab === 'sprint') return score.prediction?.sprintPredictions;
    return score.prediction?.featurePredictions || score.prediction?.predictions;
  };

  // Which result object to use based on active tab
  const getTabResult = () => {
    if (!raceResult) return null;
    if (activeTab === 'sprint') return raceResult.sprintResult || raceResult;
    return raceResult;
  };

  const columns = activeTab === 'sprint' ? SPRINT_COLUMNS : FEATURE_COLUMNS;
  const tabResult = getTabResult();
  const isSprint = selectedRace?.isSprint;

  // ── Loading / error states ──

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading races...</div>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="max-w-7xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 text-gray-900">2026 Race Results</h1>

      {/* Race selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Race</label>
        <select
          value={selectedRaceId}
          onChange={e => setSelectedRaceId(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="">— Select a race —</option>
          {races.map(race => {
            const date = new Date(race.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
            const status = race.status === 'completed' ? 'Completed' : 'Tips Closed';
            const sprint = race.isSprint ? ' 🏃' : '';
            return (
              <option key={race._id} value={race._id}>
                {race.raceName}{sprint} — {date} ({status})
              </option>
            );
          })}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>
      )}

      {!selectedRaceId ? (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-lg">
          Select a race above to view results.
        </div>
      ) : scoresLoading ? (
        <div className="text-center py-12 text-gray-500">Loading results...</div>
      ) : (
        <>
          {/* Race header */}
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{selectedRace?.raceName}</h2>
            {isSprint && (
              <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded-full">
                🏃 Sprint Weekend
              </span>
            )}
            {!raceResult && (
              <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                Results Pending
              </span>
            )}
          </div>

          {/* Sprint / Feature tabs — only shown for sprint weekends */}
          {isSprint && (
            <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
              {['feature', 'sprint'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'feature' ? '🏁 Feature Race' : '⚡ Sprint Race'}
                </button>
              ))}
            </div>
          )}

          {/* Tips-closed-but-no-results banner */}
          {!raceResult && selectedRace && (() => {
            const isCompleted = selectedRace.status === 'completed' || new Date(selectedRace.date) < new Date();
            if (!isCompleted) {
              return (
                <div className="bg-orange-50 border border-orange-300 text-orange-700 px-4 py-3 rounded-lg mb-4">
                  <strong>Tips Closed</strong> — Race results not yet available.
                </div>
              );
            }
            return null;
          })()}

          {/* Predictions table */}
          {raceScores.length > 0 ? (
            <div className="mb-8">
              <h3 className="text-base font-medium text-gray-700 mb-2">
                Player Predictions &amp; Scores
                {isSprint && <span className="ml-2 text-sm text-gray-400">— {activeTab === 'feature' ? 'Feature Race' : 'Sprint Race'}</span>}
              </h3>

              <div className="bg-white shadow rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {/* Sticky user column */}
                        <th className="sticky left-0 z-30 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[130px]">
                          Player
                        </th>
                        {columns.map(col => (
                          <th key={col.key} className="px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                            {col.label}
                          </th>
                        ))}
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">

                      {/* Results row */}
                      {tabResult && (
                        <tr className="bg-blue-50 border-b-2 border-blue-200">
                          <td className="sticky left-0 z-20 bg-blue-50 px-3 py-3 text-xs font-bold text-blue-800 border-r border-gray-200 min-w-[130px]">
                            ✦ Official Result
                          </td>
                          {columns.map(col => (
                            <td key={col.key} className="px-2 py-3 text-center bg-blue-50">
                              {renderResultCell(tabResult, col.key, col.isDriver)}
                            </td>
                          ))}
                          <td className="px-3 py-3 text-center text-blue-800 font-semibold bg-blue-50">—</td>
                        </tr>
                      )}

                      {/* User rows */}
                      {[...raceScores]
                        .sort((a, b) => b.score - a.score)
                        .map((score, idx) => {
                          const preds = getPreds(score);
                          const badges = getUserBadges(score.user);

                          return (
                            <tr key={score.username || idx} className="hover:bg-gray-50 transition-colors">
                              <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 px-3 py-3 border-r border-gray-200 min-w-[130px]">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-400 w-5 text-right">{idx + 1}</span>
                                  <div className="flex flex-col gap-0.5 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 truncate" title={score.username}>
                                      {score.username}
                                    </span>
                                    {badges.length > 0 && (
                                      <div className="flex gap-1 flex-wrap">
                                        {badges.map((b, bi) => <BadgePill key={bi} badge={b} />)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {columns.map(col => {
                                const predVal   = getPredValue(preds, col.key);
                                const resultVal = getResultValue(tabResult, col.key);
                                return (
                                  <td key={col.key} className="px-2 py-3 text-center">
                                    {renderCell(predVal, resultVal, col.isDriver, col.key, preds, tabResult)}
                                  </td>
                                );
                              })}

                              <td className="px-3 py-3 text-center">
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className="text-base font-bold text-gray-900">{score.score}</span>
                                  <button
                                    onClick={() => setDetailScore(score)}
                                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                                  >
                                    Details
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
              No predictions submitted for this race yet.
            </div>
          )}

          {/* Official results panel */}
          {raceResult && (
            <div className="mb-8 bg-white shadow rounded-xl p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4">
                Official Results — {activeTab === 'sprint' ? 'Sprint' : 'Feature'} Race
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Podium */}
                <div className="col-span-2 md:col-span-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Podium</p>
                  <div className="flex gap-3">
                    {[0,1,2].map(i => {
                      const driver = normalizeDriver(tabResult?.podium?.[i]);
                      const bgColors = ['bg-yellow-100 border-yellow-300', 'bg-gray-100 border-gray-300', 'bg-amber-100 border-amber-300'];
                      return (
                        <div key={i} className={`flex-1 border rounded-lg p-3 text-center ${bgColors[i]}`}>
                          <div className="text-xs font-bold text-gray-500 mb-1">P{i+1}</div>
                          <div className="text-sm font-semibold text-gray-900">{driver || '—'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {[
                  { label: 'Pole Position',      val: normalizeDriver(tabResult?.polePosition) },
                  { label: 'Fastest Lap',        val: normalizeDriver(tabResult?.fastestLap) },
                  { label: 'First Retirement',   val: normalizeDriver(tabResult?.firstRetirement) },
                  { label: 'Most Places Gained', val: normalizeDriver(tabResult?.mostPlacesGained) },
                  { label: 'Driver H2H Winner',  val: normalizeDriver(tabResult?.headToHead?.drivers?.winner) },
                  { label: 'Team H2H Winner',    val: normalizeTeam(tabResult?.headToHead?.teams?.winner) },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-900">{item.val || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Badge legend */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Marquee Race Badge Legend</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(MARQUEE_RACES).map(([key, badge]) => (
                <div key={key} className="flex items-center gap-2 text-sm text-blue-700">
                  <BadgePill badge={badge} />
                  <span>{badge.title}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Score Details Modal */}
      {detailScore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900">{detailScore.username}'s Score Breakdown</h2>
                <button onClick={() => setDetailScore(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 mb-4 flex justify-between items-center">
                <span className="font-medium text-gray-700">Total Score</span>
                <span className="text-2xl font-bold text-blue-600">{detailScore.score} pts</span>
              </div>

              {/* Show both feature and sprint breakdowns if sprint weekend */}
              {[
                { label: '🏁 Feature Race', preds: detailScore.prediction?.featurePredictions || detailScore.prediction?.predictions, cols: FEATURE_COLUMNS },
                ...(isSprint ? [{ label: '⚡ Sprint Race', preds: detailScore.prediction?.sprintPredictions, cols: SPRINT_COLUMNS }] : []),
              ].map(section => (
                <div key={section.label} className="mb-4">
                  {isSprint && <h3 className="font-semibold text-gray-700 mb-2">{section.label}</h3>}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <th className="px-3 py-2 text-left">Category</th>
                        <th className="px-3 py-2 text-left">Your Pick</th>
                        <th className="px-3 py-2 text-left">Actual</th>
                        <th className="px-3 py-2 text-center">Pts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {section.cols.map(col => {
                        const predVal   = getPredValue(section.preds, col.key);
                        const resultVal = getResultValue(raceResult, col.key);
                        const accuracy  = predVal && raceResult ? getCellAccuracy(predVal, resultVal, col.key) : null;
                        const display   = col.isDriver ? normalizeDriver(predVal) : normalizeTeam(predVal);
                        const actual    = col.isDriver ? normalizeDriver(resultVal) : normalizeTeam(resultVal);

                        // Simple points calculation for display (backend is authoritative)
                        let pts = 0;
                        if (accuracy === 'correct') {
                          if (col.key === 'polePosition') pts = 2;
                          else if (col.key.startsWith('podium.')) {
                            const i = parseInt(col.key.split('.')[1]);
                            pts = [6, 4, 2, 1, 1][i] ?? 1;
                          }
                          else pts = 1;
                        } else if (accuracy === 'partial') {
                          pts = 1;
                        }

                        return (
                          <tr key={col.key} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{col.label}</td>
                            <td className="px-3 py-2 text-gray-900">
                              <span>{display || '—'}</span>
                              {accuracy && <AccuracyDot status={accuracy} />}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{actual || '—'}</td>
                            <td className="px-3 py-2 text-center font-bold text-gray-900">{raceResult ? pts : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="mt-4 text-right">
                <button
                  onClick={() => setDetailScore(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Results2026;